export type Locale = 'es' | 'en';

export const LOCALES: Locale[] = ['es', 'en'];
export const DEFAULT_LOCALE: Locale = 'es';
export const LOCALE_STORAGE_KEY = 'codiaj-locale';

/**
 * Tipo Dictionary — estructura completa de todas las Translation_Keys.
 * Si falta cualquier clave en es.ts o en.ts, TypeScript produce un error
 * de compilación.
 */
export interface Dictionary {
  nav: {
    services: string;
    cases: string;
    about: string;
    contact: string;
    blog: string;
    cta: string;
    changeLangEs: string;
    changeLangEn: string;
  };
  hero: {
    badge: string;
    h1Part1: string;
    h1Accent: string;
    h1Part2: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    pillars: [
      { key: string; value: string },
      { key: string; value: string },
      { key: string; value: string },
    ];
  };
  manifiesto: {
    heading: string;
    headingAccent: string;
    left: string;
    right: string;
    rightAccent: string;
  };
  valor: {
    overline: string;
    heading: string;
    headingAccent: string;
    cards: [
      { title: string; body: string },
      { title: string; body: string },
      { title: string; body: string },
    ];
  };
  proceso: {
    overline: string;
    heading: string;
    headingAccent: string;
    steps: [
      { title: string; body: string; time: string },
      { title: string; body: string; time: string },
      { title: string; body: string; time: string },
      { title: string; body: string; time: string },
    ];
  };
  planes: {
    overline: string;
    heading: string;
    headingAccent: string;
    items: [
      { name: string; tag: string; sub: string; features: string[] },
      { name: string; tag: string; sub: string; features: string[] },
      { name: string; tag: string; sub: string; features: string[] },
    ];
  };
  testimonios: {
    overline: string;
    heading: string;
    headingAccent: string;
    items: [
      { q: string; who: string; role: string },
      { q: string; who: string; role: string },
      { q: string; who: string; role: string },
      { q: string; who: string; role: string },
    ];
  };
  faq: {
    overline: string;
    heading: string;
    headingAccent: string;
    items: Array<{ q: string; a: string }>;
  };
  contacto: {
    overline: string;
    heading: string;
    headingAccent: string;
    subtitle: string;
    fields: {
      nombre: { label: string; placeholder: string };
      email: { label: string; placeholder: string };
      telefono: { label: string; placeholder: string };
      mensaje: { label: string; placeholder: string };
      legal: string;
    };
    submit: string;
    headingSecondary: string;
  };
  footer: {
    tagline: string;
    columns: {
      nav: { title: string; items: { inicio: string; servicios: string; casos: string; acerca: string; contacto: string; blog: string } };
      legal: { title: string; items: { avisoLegal: string; consentimiento: string; accesibilidad: string; reembolso: string; privacidad: string; terminos: string; cookies: string } };
      contacto: { title: string };
    };
    copyright: string;
    madeBy: string;
  };
  cookies: {
    message: string;
    linkLabel: string;
    accept: string;
    reject: string;
    configure: string;
    save: string;
    settings: string;
    ariaLabel: string;
    ariaAccept: string;
    ariaReject: string;
    ariaConfigure: string;
    categories: {
      necessary: { title: string; desc: string };
      analytics: { title: string; desc: string };
      marketing: { title: string; desc: string };
    };
  };
  metadata: {
    home: { title: string; description: string };
    servicios: { title: string; description: string };
    acercaDe: { title: string; description: string };
    contacto: { title: string; description: string };
    casosExito: { title: string; description: string };
    blog: { title: string; description: string };
  };
  casos: {
    overline: string;
    heading: string;
    headingAccent: string;
    cta: string;
  };
  acercaDe: {
    hero: {
      overline: string;
      h1Part1: string;
      h1Accent: string;
      h1Part2: string;
      subtitle: string;
      ctaPrimary: string;
      ctaSecondary: string;
    };
    pillars: {
      overline: string;
      heading: string;
      headingAccent: string;
      items: [
        { title: string; body: string },
        { title: string; body: string },
        { title: string; body: string },
      ];
    };
    historia: {
      overline: string;
      heading: string;
      p1: string;
      p2: string;
      p3: string;
    };
    stats: [
      { value: string; label: string },
      { value: string; label: string },
      { value: string; label: string },
      { value: string; label: string },
    ];
    cta: {
      heading: string;
      headingAccent: string;
      subtitle: string;
      button: string;
    };
  };
  servicios: {
    hero: {
      title: string;
      titleAccent: string;
      subtitle: string;
      billing: {
        monthly: string;
        annual: string;
        save: string;
      };
    };
    plans: {
      semilla: {
        name: string;
        desc: string;
        price: string;
        sub: string;
        features: string[];
        button: string;
      };
      brota: {
        tag: string;
        name: string;
        desc: string;
        priceMonthly: string;
        priceAnnual: string;
        subMonthly: string;
        subAnnual: string;
        features: string[];
        button: string;
      };
      personalizado: {
        name: string;
        desc: string;
        price: string;
        sub: string;
        quote: string;
        button: string;
      };
    };
    extras: {
      title: string;
      subtitle: string;
      sections: Array<{
        title: string;
        subtitle: string;
        items: Array<{
          name: string;
          description: string;
          price: string;
        }>;
      }>;
      table: {
        extra: string;
        description: string;
        cost: string;
      };
      custom: {
        text: string;
        button: string;
      };
    };
    faq: {
      title: string;
      items: Array<{ q: string; a: string }>;
      cta: string;
    };
  };
  casosExito: {
    hero: { overline: string; h1Part1: string; h1Accent: string; subtitle: string };
    filters: { label: string; todos: string; corporativa: string; restaurante: string; artista: string; seguridad: string };
    empty: string;
    card: { view: string };
    cta: { headingPart1: string; headingAccent: string; subtitle: string; button: string };
  };
  casoDetail: {
    back: string;
    summary: string;
    sidebar: { duration: string; type: string; stack: string; deliverables: string };
    nav: { prev: string; next: string };
    cta: { headingPart1: string; headingAccent: string; subtitle: string; button: string };
  };
  blog: {
    hero: { overline: string; title: string; titleAccent: string };
    featured: { overline: string; readMore: string };
    list: { overline: string; posts: string; readMore: string };
    post: { back: string; related: string; readMore: string };
    filters: { label: string; todos: string };
    search: { placeholder: string };
    pagination: { loadMore: string; noMore: string };
  };
  legal: {
    overline: string;
    toc: string;
    updated: string;
    questions: string;
  };
}
